import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, MessageSquare, Trophy, Check } from "lucide-react";
import { analytics } from "../lib/analytics";
import { useAppMode } from "../hooks";
import { ProviderIcon } from "./ProviderIcon";
import { RankingSlot } from "./RankingSlot";
import { MODEL_METADATA, type LLMModel, type LLMProvider } from "@pick-three-ai/constants";
import type { TimeHorizon, AggregatedModelRanking, AggregatedRankingsResponse } from "@pick-three-ai/types";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

const TIME_HORIZONS: { value: TimeHorizon; label: string }[] = [
  { value: "day", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "year", label: "This year" },
  { value: "all", label: "All time" },
];

function getRankBadgeClass(rank: number): string {
  switch (rank) {
    case 1:
      return "rank-badge-1";
    case 2:
      return "rank-badge-2";
    case 3:
      return "rank-badge-3";
    default:
      return "bg-white/10 text-foreground";
  }
}

function getModelProvider(model: string): LLMProvider | null {
  const metadata = MODEL_METADATA[model as LLMModel];
  return metadata?.provider ?? null;
}

const FEEDBACK_MAX_LENGTH = 1000;

function GenericRankingsComingSoon() {
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const charsRemaining = FEEDBACK_MAX_LENGTH - message.length;
  const isOverLimit = charsRemaining < 0;

  const handleSubmit = async () => {
    if (!message.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: message.trim() }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(data.error || "Failed to send feedback");
      }

      analytics.feedbackSubmitted(message.length);
      setIsSuccess(true);
    } catch (err) {
      console.error("Feedback error:", err);
      setError(err instanceof Error ? err.message : "Failed to send feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="h-full flex flex-col overflow-hidden">
      {/* Page Header */}
      <header className="flex-shrink-0 pt-6 sm:pt-8 pb-2 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-6xl font-normal italic tracking-tight text-gradient font-display">
          Rankings Coming Soon
        </h1>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          Back to Pick Three
        </Link>
      </header>

      {/* Coming Soon Content */}
      <div className="flex-1 flex flex-col items-center justify-start px-4 pb-8 mt-5">
        <div className="w-full max-w-md space-y-8">
          {/* Coming Soon Message */}


          {/* Inline Feedback Form */}
          <div className="glass-strong rounded-2xl p-5 sm:p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-accent/20 text-accent">
                <MessageSquare className="w-5 h-5" />
              </div>
              <h3 className="text-base font-semibold text-foreground">Share Feedback</h3>
            </div>

            {isSuccess ? (
              <div className="flex flex-col items-center justify-center py-6 gap-3">
                <div className="p-3 rounded-full bg-green-500/20 text-green-400">
                  <Check className="w-6 h-6" />
                </div>
                <p className="text-foreground font-medium">Thanks for your feedback!</p>
              </div>
            ) : (
              <>
                <div className="relative">
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="What would you like to see here?"
                    disabled={isSubmitting}
                    rows={4}
                    className={`
                      w-full px-4 py-3 rounded-xl
                      bg-white/5 border border-white/10
                      text-foreground placeholder:text-muted-foreground
                      focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/30
                      disabled:opacity-50 disabled:cursor-not-allowed
                      resize-none transition-colors text-sm
                      ${isOverLimit ? "border-red-500/50 focus:border-red-500/50 focus:ring-red-500/30" : ""}
                    `}
                  />
                  
                  {/* Character count */}
                  <div className={`absolute bottom-3 right-3 text-xs ${
                    isOverLimit ? "text-red-400" : charsRemaining < 100 ? "text-yellow-400" : "text-muted-foreground"
                  }`}>
                    {charsRemaining}
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-400">{error}</p>
                )}

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!message.trim() || isOverLimit || isSubmitting}
                  className="
                    w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl
                    text-sm font-semibold
                    bg-accent text-accent-foreground
                    hover:bg-accent-hover
                    disabled:opacity-40 disabled:cursor-not-allowed
                    transition-all duration-200
                  "
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Feedback"
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

export function RankingsPage() {
  const [timeHorizon, setTimeHorizon] = useState<TimeHorizon>("month");
  const [rankings, setRankings] = useState<AggregatedModelRanking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isGeneric } = useAppMode();

  // Track page view
  useEffect(() => {
    analytics.pageView("rankings");
  }, []);

  // Fetch rankings when time horizon changes
  useEffect(() => {
    const fetchRankings = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_URL}/api/rankings/aggregate?horizon=${timeHorizon}`);
        
        if (!response.ok) {
          throw new Error("Failed to fetch rankings");
        }

        const data: AggregatedRankingsResponse = await response.json();
        setRankings(data.rankings);
      } catch (err) {
        console.error("Rankings fetch error:", err);
        setError(err instanceof Error ? err.message : "Failed to load rankings");
      } finally {
        setIsLoading(false);
      }
    };

    fetchRankings();
  }, [timeHorizon]);

  // If in generic mode, show coming soon page with inline feedback
  if (isGeneric) {
    return <GenericRankingsComingSoon />;
  }

  // Split rankings into top 3 and rest
  const topThree = rankings.slice(0, 3);
  const restOfRankings = rankings.slice(3);

  return (
    <main className="h-full flex flex-col overflow-hidden">
      {/* Page Header */}
      <header className="flex-shrink-0 pt-6 sm:pt-8 pb-2 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-6xl font-normal italic tracking-tight text-gradient font-display">
          Model Rankings
        </h1>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          Back to Pick Three AI
        </Link>
      </header>

      {/* Time Horizon Selector */}
      <div className="flex-shrink-0 flex justify-center px-4 py-4">
        <div className="inline-flex gap-1 p-1 glass-strong rounded-xl">
          {TIME_HORIZONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setTimeHorizon(value)}
              className={`
                px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200
                ${timeHorizon === value 
                  ? "bg-accent text-accent-foreground" 
                  : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                }
              `}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto scrollbar-none px-4 pb-8">
        <div className="w-full max-w-md mx-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center py-16 space-y-4">
              <p className="text-muted-foreground">{error}</p>
              <button
                type="button"
                onClick={() => setTimeHorizon(timeHorizon)}
                className="px-4 py-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.08)] text-sm font-medium transition-colors"
              >
                Try again
              </button>
            </div>
          ) : rankings.length === 0 ? (
            <div className="text-center py-16 space-y-4">
              <Trophy className="w-12 h-12 text-muted-foreground mx-auto" />
              <p className="text-muted-foreground">No rankings yet for this time period.</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-accent hover:bg-accent-hover text-accent-foreground font-semibold text-sm transition-all duration-200"
              >
                Be the first to rank
              </Link>
            </div>
          ) : (
            <div className="space-y-6 pt-4">
              {/* Top 3 - Using RankingSlot components with halos */}
              {topThree.length > 0 && (
                <div className="space-y-3">
                  {topThree.map((ranking) => {
                    const haloClass = ranking.rank === 1 ? "halo-gold" : ranking.rank === 2 ? "halo-silver" : "halo-bronze";
                    return (
                      <div key={ranking.model} className="relative">
                        <div className={haloClass}>
                          <RankingSlot
                            rank={ranking.rank as 1 | 2 | 3}
                            model={ranking.model}
                            blurb=""
                            onModelClick={() => {}}
                            onBlurbChange={() => {}}
                            isReadOnly={true}
                            isGeneric={false}
                          />
                        </div>
                        {/* Score overlay */}
                        <div className="absolute top-1/2 right-4 -translate-y-1/2 text-right pointer-events-none z-10">
                          <div className="font-bold text-base text-white/90">
                            {ranking.score}
                          </div>
                          <div className="text-xs font-medium text-white/70">
                            {ranking.voteCount} {ranking.voteCount === 1 ? "vote" : "votes"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Rest of Rankings - Simple list */}
              {restOfRankings.length > 0 && (
                <div className="space-y-2 pt-2">
                  {restOfRankings.map((ranking) => {
                    const provider = getModelProvider(ranking.model);
                    
                    return (
                      <div
                        key={ranking.model}
                        className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl glass hover:bg-[rgba(255,255,255,0.06)] transition-all duration-200"
                      >
                        {/* Rank Badge */}
                        <div className={`
                          flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center font-bold text-sm sm:text-base
                          ${getRankBadgeClass(ranking.rank)}
                        `}>
                          {ranking.rank}
                        </div>

                        {/* Model Info */}
                        <div className="flex-1 min-w-0 flex items-center gap-3">
                          {provider && (
                            <ProviderIcon provider={provider} size={20} className="flex-shrink-0 text-muted-foreground" />
                          )}
                          <span className="font-medium truncate text-foreground/90">
                            {ranking.model}
                          </span>
                        </div>

                        {/* Score */}
                        <div className="flex-shrink-0 text-right">
                          <div className="font-bold text-sm sm:text-base text-muted-foreground">
                            {ranking.score}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {ranking.voteCount} {ranking.voteCount === 1 ? "vote" : "votes"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer with feedback link */}
      <footer className="flex-shrink-0 py-3 sm:py-4 text-center">
        <Link
          to="/"
          state={{ openFeedback: true }}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <MessageSquare className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          Share feedback
        </Link>
      </footer>
    </main>
  );
}
